// ─── Tunable parameters ───────────────────────────────────────────────────────

// Size (in CSS pixels) of each Bayer cell.
// Larger = chunkier visible blocks; smaller = finer grain.
const TILE_SIZE = 1

// Total time (ms) for the full reveal.
const TOTAL_MS = 2000

// How long each layer takes to fade in (ms).
const CROSSFADE_MS = 50

// Cumulative Bayer threshold per stage (out of 16 cells in the 4×4 matrix).
// First entry ≈ almost fully blocked; last entry = 16 = fully revealed.
// Adjust the distribution to change how the density ramps up.
const THRESHOLDS = [1, 4, 7, 10, 13, 16]

// ─────────────────────────────────────────────────────────────────────────────

// Bayer 4×4 ordered-dither matrix. Controls which pixels appear in which stage.
// Rearranging this changes the spatial pattern of the teleport effect.
const BAYER = [
  [ 0,  8,  2, 10],
  [12,  4, 14,  6],
  [ 3, 11,  1,  9],
  [15,  7, 13,  5],
]

const STAGE_MS = TOTAL_MS / THRESHOLDS.length

// Module-level cache — masks are generated once and reused across all calls.
let _masks = null

// Returns an array of PNG data URIs, one per stage.
// Each mask shows only the *incremental* pixels added at that stage, so the
// layers are fully disjoint — no pixel is covered by more than one mask.
function getMasks() {
  if (_masks) return _masks

  const tileSize  = 4 * TILE_SIZE
  const prevThresholds = [0, ...THRESHOLDS.slice(0, -1)]

  _masks = THRESHOLDS.map((threshold, i) => {
    const canvas = document.createElement("canvas")
    canvas.width  = tileSize
    canvas.height = tileSize
    const ctx = canvas.getContext("2d")

    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        const val = BAYER[row][col]
        if (val >= prevThresholds[i] && val < threshold) {
          ctx.fillStyle = "white"
          ctx.fillRect(col * TILE_SIZE, row * TILE_SIZE, TILE_SIZE, TILE_SIZE)
        }
        // Transparent elsewhere — CSS mask-mode:alpha hides those pixels.
      }
    }

    return canvas.toDataURL()
  })

  return _masks
}

/**
 * Reveals an image into `container` using a Bayer-dithered dissolve.
 *
 * Stacks one <img> per stage, each masked to show only the pixels that
 * appear at that stage. Each layer fades in with a staggered delay so
 * the image materialises over TOTAL_MS milliseconds.
 *
 * Any child element marked with data-thumbnail-spinner is preserved during
 * the animation and removed only when the final image is placed, allowing a
 * spinner to remain visible throughout the reveal.
 *
 * Returns { cancel, done }:
 *   cancel — call to abort mid-reveal (e.g. when the tile is removed).
 *   done   — Promise that resolves once the final image is in place (or when
 *            cancelled, to avoid dangling promise references).
 *
 * @param {string}      imgSrc
 * @param {HTMLElement} container
 * @returns {{ cancel: () => void, done: Promise<void> }}
 */
export function dissolveReveal(imgSrc, container) {
  const masks    = getMasks()
  const tileSize = 4 * TILE_SIZE

  // Clear existing content but preserve any spinner overlay so it remains
  // visible above the dissolve layers throughout the animation.
  Array.from(container.children).forEach(child => {
    if (!child.dataset.thumbnailSpinner) child.remove()
  })
  container.style.position = "relative"

  const handles  = []
  let cancelled  = false
  let resolveDone
  const done = new Promise(resolve => { resolveDone = resolve })

  masks.forEach((maskDataUri, i) => {
    const img = document.createElement("img")
    img.src       = imgSrc
    img.draggable = false
    img.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: 0;
      transition: opacity ${CROSSFADE_MS}ms ease-in-out;
      -webkit-mask-image: url("${maskDataUri}");
              mask-image: url("${maskDataUri}");
      -webkit-mask-repeat: repeat;
              mask-repeat: repeat;
      -webkit-mask-size: ${tileSize}px ${tileSize}px;
              mask-size: ${tileSize}px ${tileSize}px;
    `
    container.appendChild(img)

    const id = setTimeout(() => {
      if (!cancelled) img.style.opacity = "1"
    }, i * STAGE_MS)
    handles.push(id)
  })

  // Once all layers have fully faded in, swap to a single clean <img>.
  // replaceChildren removes the spinner overlay at the same time, so the
  // spinner disappears exactly when the final image takes its place.
  const finalId = setTimeout(() => {
    if (cancelled) return
    const finalImg = document.createElement("img")
    finalImg.src       = imgSrc
    finalImg.className = "w-full h-full object-cover"
    finalImg.draggable = false
    container.replaceChildren(finalImg)
    container.style.position = ""
    resolveDone()
  }, THRESHOLDS.length * STAGE_MS + CROSSFADE_MS)
  handles.push(finalId)

  return {
    cancel: () => {
      cancelled = true
      handles.forEach(clearTimeout)
      resolveDone() // resolve so .then() callbacks don't hold stale references
    },
    done,
  }
}
